import { IconCheck, IconCopy, IconExternalLink, IconRefresh } from "@tabler/icons-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, STAFF_KEY_STORAGE, staffApi, staffAuthApi, type AccessApp, type AuthConfig, type Proposal, type ProposalQuestion, type ReplyVia, type StaffEvent, type StaffIdentity, type StaffMe, type StaffMessage, type StaffTripDetail, type ThreadMessage, type TripSummary } from "../api";
import { formatDateTime, formatMoney, relativeTime } from "../format";
import { ErrorNote } from "../components/ui";
import { useStaffData } from "./staffData";
import { LiveSimulator, MembersSection, NoticesTab, TripNoticesSection } from "./StaffNotices";
import { EmergenciesTab, OnCallTab, ProgrammeBlock } from "./StaffClub";
import { MonitoringTab } from "./StaffMonitoring";

type Tab = "outbox" | "threads" | "proposals" | "identities" | "trips" | "notices" | "emergencies" | "oncall" | "events" | "monitoring" | "status";
const TABS: { id: Tab; label: string }[] = [
  { id: "outbox", label: "Outbox" },
  { id: "threads", label: "Threads" },
  { id: "proposals", label: "Proposals" },
  { id: "identities", label: "Identities" },
  { id: "trips", label: "Trips" },
  { id: "notices", label: "Notices" },
  { id: "emergencies", label: "Emergencies" },
  { id: "oncall", label: "On call" },
  { id: "events", label: "Events" },
  { id: "monitoring", label: "Monitoring" },
  { id: "status", label: "Status" },
];

const STAFF_RETURN_TO = "/staff/";
const LOGIN_ERROR_PARAM = "login_error";

/** What `/staff/?login_error=<code>` means to the person, for the codes the server sends. */
function loginErrorText(code: string): string {
  if (code === "no_access") return "Your account has no role on the customer area; ask an administrator to grant one in Resosys Access";
  return `Sign-in failed: ${code}`;
}

function loginErrorFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get(LOGIN_ERROR_PARAM);
  } catch {
    return null;
  }
}

/** The team's view of the simulated channels: codes and links that would have been sent, identities, trips, events. */
export function StaffPage() {
  const [tab, setTab] = useState<Tab>("outbox");
  const [needKey, setNeedKey] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  // Resosys Access: the server's auth mode, who is signed in and the other back-offices.
  const [auth, setAuth] = useState<AuthConfig | null>(null);
  const [me, setMe] = useState<StaffMe | null>(null);
  const [apps, setApps] = useState<AccessApp[]>([]);
  const [needSignIn, setNeedSignIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [loginError] = useState<string | null>(() => loginErrorFromUrl());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let config: AuthConfig = { mode: "local" };
      try {
        config = await staffAuthApi.config();
      } catch {
        config = { mode: "local" };
      }
      if (cancelled) return;
      setAuth(config);
      if (config.mode === "central") {
        try {
          const who = await staffAuthApi.me();
          if (cancelled) return;
          setMe(who);
          setNeedSignIn(false);
          staffAuthApi
            .apps()
            .then((res) => {
              if (!cancelled) setApps(res.apps.filter((a) => !a.current));
            })
            .catch(() => undefined);
        } catch (err) {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 401) setNeedSignIn(true);
        }
      }
      if (!cancelled) setAuthReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onError = useCallback((err: unknown) => {
    if (!(err instanceof ApiError)) return;
    if (err.code === "staff_key_required") setNeedKey(true);
    else if (err.code === "sign_in_required") setNeedSignIn(true);
  }, []);

  const saveKey = (e: FormEvent) => {
    e.preventDefault();
    try {
      sessionStorage.setItem(STAFF_KEY_STORAGE, keyInput.trim());
    } catch {
      // ignore
    }
    setNeedKey(false);
    setKeyInput("");
  };

  const signIn = () => {
    if (auth?.mode !== "central") return;
    location.assign(`${auth.loginUrl}?returnTo=${encodeURIComponent(STAFF_RETURN_TO)}`);
  };

  const signOut = async () => {
    try {
      const res = await staffAuthApi.logout();
      if (res.redirect) location.assign(res.redirect);
      else location.reload();
    } catch {
      location.reload();
    }
  };

  const central = auth?.mode === "central" ? auth : null;

  if (central && needSignIn) {
    return (
      <main className="page staff">
        <h1>Staff · My trips</h1>
        <section className="card stack">
          <p>Sign in with your Ideastation account (Google) to see the outbox, trips and notices.</p>
          {loginError && <ErrorNote>{loginErrorText(loginError)}</ErrorNote>}
          <div>
            <button type="button" className="button button-primary" onClick={signIn}>
              Sign in
            </button>
          </div>
        </section>
      </main>
    );
  }

  // Pause the outbox polling until the auth mode is known and nobody has to sign in or enter a key.
  const paused = needKey || needSignIn || (!authReady && auth === null);

  return (
    <main className="page staff">
      {central && me && (
        <div className="staff-user-bar">
          <span>
            <strong>{me.user.name || me.user.email}</strong>
            {me.user.name ? <span className="muted"> · {me.user.email}</span> : null}
          </span>
          <span className="row-buttons">
            {central.portalUrl && <a href={central.portalUrl}>All apps</a>}
            {apps.map((a) => (
              <a key={a.key} href={a.url}>
                {a.name}
              </a>
            ))}
            <a href={central.accountUrl}>Account</a>
            <button type="button" className="link-button" onClick={() => void signOut()}>
              Sign out
            </button>
          </span>
        </div>
      )}
      <h1>My trips · Staff</h1>
      <p className="muted small">Messages the customers would have received, with their codes and links. Nothing is sent from staging.</p>
      {needKey && (
        <form className="card form" onSubmit={saveKey}>
          <label className="field">
            <span className="field-label">Staff key</span>
            <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} autoFocus />
            <span className="field-hint">The key is kept in this tab only (sessionStorage).</span>
          </label>
          <button type="submit" className="button button-primary">
            Use key
          </button>
        </form>
      )}
      <nav className="tabs" aria-label="Staff sections">
        {TABS.map((x) => (
          <button key={x.id} type="button" className={tab === x.id ? "active" : ""} onClick={() => setTab(x.id)}>
            {x.label}
          </button>
        ))}
      </nav>
      {tab === "outbox" && <OutboxTab onError={onError} paused={paused} />}
      {tab === "threads" && <ThreadsTab onError={onError} />}
      {tab === "proposals" && <ProposalsTab onError={onError} />}
      {tab === "identities" && <IdentitiesTab onError={onError} />}
      {tab === "trips" && <TripsTab onError={onError} />}
      {tab === "notices" && <NoticesTab onError={onError} />}
      {tab === "emergencies" && <EmergenciesTab onError={onError} />}
      {tab === "oncall" && <OnCallTab onError={onError} />}
      {tab === "events" && <EventsTab onError={onError} />}
      {tab === "monitoring" && <MonitoringTab onError={onError} />}
      {tab === "status" && <StatusTab onError={onError} />}
    </main>
  );
}

function OutboxTab({ onError, paused }: { onError: (err: unknown) => void; paused: boolean }) {
  const [tick, setTick] = useState(0);
  const { data, error, reload } = useStaffData(() => staffApi.outbox(), onError, [tick]);

  // Auto-refresh every 5 s while the tab is visible.
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") setTick((n) => n + 1);
    }, 5000);
    return () => window.clearInterval(id);
  }, [paused]);

  const messages = [...(data?.messages ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <section>
      <div className="section-title">
        <h2>Outbox ({messages.length})</h2>
        <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
          <IconRefresh size={18} />
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
      <div className="stack">
        {messages.map((m) => (
          <OutboxCard key={m.id} message={m} onRead={reload} />
        ))}
        {data && messages.length === 0 && <p className="muted">No messages yet.</p>}
      </div>
    </section>
  );
}

function OutboxCard({ message: m, onRead }: { message: StaffMessage; onRead: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!m.code) return;
    try {
      await navigator.clipboard.writeText(m.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked: the code is selectable anyway.
    }
  };
  const markRead = async () => {
    await staffApi.markRead(m.id).catch(() => undefined);
    onRead();
  };
  return (
    <article className={`card outbox-card ${m.readAt ? "read" : ""}`}>
      <div className="outbox-meta">
        <span className={`chip chip-${m.channel}`}>{m.channel}</span>
        <span className="chip">{m.brand}</span>
        <span className="chip">{m.kind}</span>
        <span className="muted small" title={formatDateTime(m.createdAt, "en")}>
          {relativeTime(m.createdAt, "en")}
        </span>
        <span className="muted small">{m.status === "blocked" ? "blocked (not on the allowlist, copy in Mailpit)" : m.status} · {m.provider}</span>
      </div>
      <p className="small">
        <strong>To:</strong> {m.to} · {m.language}
        {m.subject ? ` · ${m.subject}` : ""}
      </p>
      {m.code && (
        <div className="code-big">
          <code>{m.code}</code>
          <button type="button" className="icon-button" onClick={() => void copy()} aria-label="Copy code">
            {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
          </button>
        </div>
      )}
      {m.link && (
        <a href={m.link} className="doc-link" target="_blank" rel="external noreferrer">
          {m.link} <IconExternalLink size={14} />
        </a>
      )}
      <details>
        <summary className="muted small">Body</summary>
        <pre className="message-pre">{m.body}</pre>
      </details>
      {!m.readAt && (
        <button type="button" className="link-button" onClick={() => void markRead()}>
          Mark as read
        </button>
      )}
    </article>
  );
}

function IdentitiesTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error } = useStaffData(() => staffApi.identities(), onError, []);
  return (
    <section>
      {error && <p className="error-note">{error}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Lang</th>
              <th>CRM contact</th>
              <th>Created</th>
              <th>Last sign-in</th>
            </tr>
          </thead>
          <tbody>
            {(data?.identities ?? []).map((i: StaffIdentity) => (
              <tr key={i.id}>
                <td>{i.email}</td>
                <td>{i.name ?? "—"}</td>
                <td>{i.phone ?? "—"}</td>
                <td>{i.language}</td>
                <td>{i.crmContactId ?? "—"}</td>
                <td>{formatDateTime(i.createdAt, "en")}</td>
                <td>{i.lastSignInAt ? formatDateTime(i.lastSignInAt, "en") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TripsTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error } = useStaffData(() => staffApi.trips(), onError, []);
  const [selected, setSelected] = useState<string | null>(null);
  const [jobs, setJobs] = useState<string | null>(null);
  const [jobsBusy, setJobsBusy] = useState(false);

  const runJobs = async () => {
    setJobsBusy(true);
    try {
      const res = await staffApi.runJobs();
      setJobs(Object.entries(res.results).map(([k, v]) => `${k}: ${v}`).join(" · "));
    } catch (err) {
      onError(err);
      setJobs(err instanceof Error ? err.message : "failed");
    } finally {
      setJobsBusy(false);
    }
  };

  return (
    <section className="stack">
      <div className="section-title">
        <h2>Trips ({data?.trips.length ?? 0})</h2>
        <div className="row-buttons">
          {jobs && <span className="muted small">{jobs}</span>}
          <button type="button" className="button button-small" disabled={jobsBusy} onClick={() => void runJobs()}>
            {jobsBusy ? "Running…" : "Run reminder jobs now"}
          </button>
        </div>
      </div>
      {error && <p className="error-note">{error}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Reference</th>
              <th>Title</th>
              <th>Brand</th>
              <th>Source</th>
              <th>Status</th>
              <th>Dates</th>
              <th>Holder</th>
              <th>Id</th>
            </tr>
          </thead>
          <tbody>
            {(data?.trips ?? []).map((x: TripSummary) => (
              <tr key={x.id} className={`clickable ${selected === x.id ? "selected" : ""}`} onClick={() => setSelected(selected === x.id ? null : x.id)}>
                <td>{x.reference}</td>
                <td>{x.title}</td>
                <td>{x.brand}</td>
                <td>{x.source}</td>
                <td>{x.status}</td>
                <td>
                  {x.startsOn ?? "—"} → {x.endsOn ?? "—"}
                </td>
                <td>{x.holderName ?? "—"}</td>
                <td>
                  <code className="small">{x.id}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <StaffTripDetailView id={selected} onError={onError} />}
    </section>
  );
}

/** One trip: payments with the simulator controls, travellers with validation, members and timeline. */
function StaffTripDetailView({ id, onError }: { id: string; onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData<StaffTripDetail>(() => staffApi.trip(id), onError, [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectSlot, setRejectSlot] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      onError(err);
      setActionError(err instanceof ApiError ? `${err.code}${err.message && err.message !== err.code ? `: ${err.message}` : ""}` : err instanceof Error ? err.message : "failed");
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="error-note">{error}</p>;
  if (!data) return <p className="muted">…</p>;
  const { trip, payments, travellers, members, timeline } = data;
  return (
    <article className="card staff-detail stack">
      <div className="section-title">
        <h2>
          {trip.reference} · {trip.title}
        </h2>
        <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
          <IconRefresh size={18} />
        </button>
      </div>
      <p className="muted small">
        {trip.brand} · {trip.source} · {trip.status} · {trip.holderName ?? "—"} · {trip.travellers} travellers · {formatMoney(trip.totalMinor, trip.currency, "en")} · <a href={`/trips/${trip.id}`} target="_blank" rel="external noreferrer">open booking page <IconExternalLink size={12} /></a>
      </p>
      {actionError && <p className="error-note">{actionError}</p>}

      <h3>Payments</h3>
      {!payments ? (
        <div className="row-buttons">
          <span className="muted small">No payment plan.</span>
          <button type="button" className="button button-small button-primary" disabled={busy !== null} onClick={() => void run("plan", () => staffApi.createPlan(trip.id))}>
            Create payment plan
          </button>
        </div>
      ) : (
        <>
          <p className="small">
            Plan <strong>{payments.plan.status}</strong> · deposit {formatMoney(payments.plan.depositMinor, payments.plan.currency, "en")} · balance {formatMoney(payments.plan.balanceMinor, payments.plan.currency, "en")} due {payments.plan.balanceDueOn}
            {payments.plan.instalments ? ` · ${payments.plan.instalments.length} instalments` : ""} · paid {formatMoney(payments.totals.paid, payments.totals.currency, "en")} · outstanding {formatMoney(payments.totals.outstanding, payments.totals.currency, "en")}
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Due</th>
                  <th>Settled</th>
                  <th>Simulate</th>
                </tr>
              </thead>
              <tbody>
                {payments.payments.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.kind}
                      {p.instalmentNo ? ` #${p.instalmentNo}` : ""}
                    </td>
                    <td>{formatMoney(p.amountMinor, p.currency, "en")}</td>
                    <td>
                      <span className={`chip chip-${p.status}`}>{p.status}</span>
                      {p.failureReason ? <span className="muted small"> {p.failureReason}</span> : ""}
                    </td>
                    <td>{p.method ?? "—"}</td>
                    <td>{p.dueOn ?? "—"}</td>
                    <td>{p.settledAt ? formatDateTime(p.settledAt, "en") : "—"}</td>
                    <td>
                      {p.kind !== "refund" && (
                        <div className="row-buttons">
                          {p.status !== "paid" && (
                            <>
                              <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void run(p.id, () => staffApi.simulatePayment(trip.id, p.id, "paid"))}>
                                Mark paid
                              </button>
                              <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void run(p.id, () => staffApi.simulatePayment(trip.id, p.id, "failed"))}>
                                Mark failed
                              </button>
                              <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void run(p.id, () => staffApi.simulatePayment(trip.id, p.id, "pending"))}>
                                Mark pending
                              </button>
                            </>
                          )}
                          {p.status === "paid" && (
                            <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void run(p.id, () => staffApi.simulatePayment(trip.id, p.id, "refund"))}>
                              Refund
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3>Travellers</h3>
      {travellers.length === 0 ? (
        <p className="muted small">No traveller slots yet (opened with the payment plan).</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Slot</th>
                <th>Name</th>
                <th>DOB</th>
                <th>Nat.</th>
                <th>Document</th>
                <th>Expiry</th>
                <th>Status</th>
                <th>Due</th>
                <th>Validate</th>
              </tr>
            </thead>
            <tbody>
              {travellers.map((tr) => (
                <tr key={tr.id}>
                  <td>{tr.slot}</td>
                  <td>{[tr.givenNames, tr.surname].filter(Boolean).join(" ") || "—"}</td>
                  <td>{tr.dateOfBirth ?? "—"}</td>
                  <td>{tr.nationality ?? "—"}</td>
                  <td>{tr.documentNumber ? `${tr.documentType ?? ""} ${tr.documentNumber}` : "—"}</td>
                  <td>{tr.documentExpiry ?? "—"}</td>
                  <td>
                    <span className={`chip chip-tr-${tr.status}`}>{tr.status}</span>
                    {tr.rejectedReason ? <span className="muted small"> {tr.rejectedReason}</span> : ""}
                  </td>
                  <td>{tr.dueOn ?? "—"}</td>
                  <td>
                    {tr.status === "submitted" && (
                      <div className="row-buttons">
                        <button type="button" className="button button-small button-primary" disabled={busy !== null} onClick={() => void run(`tr${tr.slot}`, () => staffApi.validateTraveller(trip.id, tr.slot, "staff"))}>
                          Validate
                        </button>
                        {rejectSlot === tr.slot ? (
                          <>
                            <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason" className="inline-input" />
                            <button type="button" className="button button-small" disabled={busy !== null || !rejectReason.trim()} onClick={() => void run(`tr${tr.slot}`, () => staffApi.validateTraveller(trip.id, tr.slot, "staff", rejectReason.trim()).then(() => { setRejectSlot(null); setRejectReason(""); }))}>
                              Reject
                            </button>
                          </>
                        ) : (
                          <button type="button" className="button button-small" disabled={busy !== null} onClick={() => setRejectSlot(tr.slot)}>
                            Reject (reason)
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TripNoticesSection trip={trip} onError={onError} />

      <LiveSimulator trip={trip} timeline={timeline} onChanged={reload} onError={onError} />

      <MembersSection trip={trip} members={members} onError={onError} />

      <details>
        <summary className="muted small">
          Raw members ({members.length}) · timeline ({timeline.length})
        </summary>
        <pre className="message-pre">{JSON.stringify({ members, timeline }, null, 2)}</pre>
      </details>
    </article>
  );
}

const EXAMPLE_OPTIONS = [
  {
    id: "classic",
    title: "Classic",
    description: "Four nights at a 4-star hotel in Ponta Delgada with transfers.",
    recommended: true,
    priceMinor: 189000,
    items: [
      { kind: "flight", title: "Lisbon → Ponta Delgada", detail: "TAP, 09:40 – 11:55", day: 0 },
      { kind: "transfer", title: "Airport → hotel", day: 0 },
      { kind: "accommodation", title: "Hotel Marina Atlântico, 4 nights", detail: "Double room, breakfast", day: 0, endDay: 4 },
      { kind: "activity", title: "Sete Cidades and Lagoa do Fogo tour", day: 1 },
      { kind: "flight", title: "Ponta Delgada → Lisbon", detail: "TAP, 12:45 – 16:50", day: 4 },
    ],
  },
  {
    id: "premium",
    title: "Premium with whale watching",
    description: "Same trip, sea-view room and a whale watching afternoon.",
    priceMinor: 229000,
    items: [
      { kind: "flight", title: "Lisbon → Ponta Delgada", detail: "TAP, 09:40 – 11:55", day: 0 },
      { kind: "transfer", title: "Airport → hotel", day: 0 },
      { kind: "accommodation", title: "Hotel Marina Atlântico, 4 nights", detail: "Sea-view room, breakfast", day: 0, endDay: 4 },
      { kind: "activity", title: "Sete Cidades and Lagoa do Fogo tour", day: 1 },
      { kind: "experience", title: "Whale watching", detail: "Afternoon, 3 hours", day: 2 },
      { kind: "flight", title: "Ponta Delgada → Lisbon", detail: "TAP, 12:45 – 16:50", day: 4 },
    ],
  },
];
const EXAMPLE_STORY = [
  { day: 0, title: "Arrival in São Miguel", text: "Land in Ponta Delgada before noon, settle into the hotel and stroll the marina." },
  { day: 1, title: "The lakes of the west", text: "Sete Cidades, its twin lakes and the viewpoints of the north coast." },
  { day: 2, title: "A free day, or the sea", text: "Furnas, the hot springs, or an afternoon with the whales." },
];

function ProposalsTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.proposals(), onError, []);
  const [composing, setComposing] = useState(false);
  const proposals = [...(data?.proposals ?? [])].sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
  const openQuestions = data?.questions ?? [];
  return (
    <section className="stack">
      <div className="section-title">
        <h2>Proposals ({proposals.length})</h2>
        <div className="row-buttons">
          <button type="button" className="button button-small button-primary" onClick={() => setComposing((v) => !v)}>
            {composing ? "Close editor" : "New proposal"}
          </button>
          <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>
      {error && <p className="error-note">{error}</p>}
      {composing && (
        <ProposalEditor
          onPublished={() => {
            setComposing(false);
            reload();
          }}
          onError={onError}
        />
      )}
      {openQuestions.length > 0 && (
        <article className="card stack">
          <h3>Open questions ({openQuestions.length})</h3>
          {openQuestions.map((q) => (
            <QuestionRow key={q.id} question={q} proposals={proposals} onAnswered={reload} onError={onError} />
          ))}
        </article>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Title</th>
              <th>Brand</th>
              <th>Holder</th>
              <th>Dates</th>
              <th>Options</th>
              <th>Valid until</th>
              <th>Sent</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((p) => (
              <ProposalRow key={p.id} proposal={p} onError={onError} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ProposalRow({ proposal: p, onError }: { proposal: Proposal; onError: (err: unknown) => void }) {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fetchLink = async () => {
    setBusy(true);
    try {
      setLink((await staffApi.proposal(p.id)).link);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr>
      <td>
        <span className={`chip chip-${p.status}`}>{p.status}</span>
      </td>
      <td>
        {p.title}
        {p.tripId && (
          <>
            {" "}
            <a href={`/trips/${p.tripId}`} target="_blank" rel="external noreferrer" className="small">
              trip <IconExternalLink size={12} />
            </a>
          </>
        )}
      </td>
      <td>{p.brand}</td>
      <td>
        {p.holderName ?? "—"} <span className="muted small">{p.holderEmail}</span>
      </td>
      <td>
        {p.startsOn ?? "—"} → {p.endsOn ?? "—"}
      </td>
      <td>
        {p.options.length}
        {p.chosenOptionId ? ` (chosen: ${p.chosenOptionId})` : ""}
      </td>
      <td>{p.validUntil ? formatDateTime(p.validUntil, "en") : "—"}</td>
      <td>{p.sentAt ? relativeTime(p.sentAt, "en") : "—"}</td>
      <td>
        {link ? (
          <a href={link} className="doc-link" target="_blank" rel="external noreferrer">
            open <IconExternalLink size={14} />
          </a>
        ) : (
          <button type="button" className="link-button" disabled={busy} onClick={() => void fetchLink()}>
            {busy ? "…" : "Get link"}
          </button>
        )}
      </td>
    </tr>
  );
}

function QuestionRow({ question: q, proposals, onAnswered, onError }: { question: ProposalQuestion; proposals: Proposal[]; onAnswered: () => void; onError: (err: unknown) => void }) {
  const [answer, setAnswer] = useState("");
  const [by, setBy] = useState("Your consultant");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const proposal = proposals.find((p) => p.id === q.proposalId);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!q.proposalId || !answer.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await staffApi.answerQuestion(q.proposalId, q.id, answer.trim(), by.trim() || "Your consultant");
      onAnswered();
    } catch (err) {
      onError(err);
      setError(err instanceof Error ? err.message : "failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="question-staff" onSubmit={(e) => void submit(e)}>
      <p className="small">
        <strong>{proposal ? `${proposal.title} · ${proposal.holderName ?? proposal.holderEmail}` : q.proposalId}</strong> <span className="muted">{relativeTime(q.askedAt, "en")}</span>
      </p>
      <p>{q.text}</p>
      <div className="row-buttons">
        <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Answer" className="inline-input grow" required />
        <input value={by} onChange={(e) => setBy(e.target.value)} placeholder="By" className="inline-input" />
        <button type="submit" className="button button-small button-primary" disabled={busy || !answer.trim()}>
          {busy ? "…" : "Answer"}
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
    </form>
  );
}

function ProposalEditor({ onPublished, onError }: { onPublished: (p: Proposal, link: string) => void; onError: (err: unknown) => void }) {
  const [form, setForm] = useState({
    brand: "azores-getaways", holderEmail: "", holderName: "", language: "en", title: "Five days in São Miguel", destination: "São Miguel, Azores", summary: "", coverUrl: "",
    startsOn: "", endsOn: "", travellers: 2, currency: "EUR", consultantName: "", validUntil: "", depositPercent: 30,
  });
  const [options, setOptions] = useState(JSON.stringify(EXAMPLE_OPTIONS, null, 2));
  const [story, setStory] = useState(JSON.stringify(EXAMPLE_STORY, null, 2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState<{ proposal: Proposal; link: string } | null>(null);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: key === "travellers" || key === "depositPercent" ? Number(e.target.value) : e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const parsedOptions: unknown = JSON.parse(options);
      const parsedStory: unknown = story.trim() ? JSON.parse(story) : undefined;
      const body: Record<string, unknown> = {
        brand: form.brand, holderEmail: form.holderEmail.trim(), language: form.language, title: form.title.trim(), travellers: form.travellers, currency: form.currency, options: parsedOptions,
        ...(form.holderName.trim() ? { holderName: form.holderName.trim() } : {}),
        ...(form.destination.trim() ? { destination: form.destination.trim() } : {}),
        ...(form.summary.trim() ? { summary: form.summary.trim() } : {}),
        ...(form.coverUrl.trim() ? { coverUrl: form.coverUrl.trim() } : {}),
        ...(form.startsOn ? { startsOn: form.startsOn } : {}),
        ...(form.endsOn ? { endsOn: form.endsOn } : {}),
        ...(form.validUntil ? { validUntil: new Date(`${form.validUntil}T23:59:59`).toISOString() } : {}),
        ...(form.consultantName.trim() ? { consultant: { name: form.consultantName.trim() } } : {}),
        ...(parsedStory ? { story: parsedStory } : {}),
        paymentTerms: { depositPercent: form.depositPercent },
      };
      const res = await staffApi.publishProposal(body);
      setPublished(res);
      onPublished(res.proposal, res.link);
    } catch (err) {
      onError(err);
      setError(err instanceof SyntaxError ? `Options/story JSON: ${err.message}` : err instanceof ApiError ? `${err.code}${err.message && err.message !== err.code ? `: ${err.message}` : ""}` : err instanceof Error ? err.message : "failed");
    } finally {
      setBusy(false);
    }
  };

  if (published) {
    return (
      <article className="card stack">
        <h3>Published</h3>
        <a href={published.link} className="doc-link" target="_blank" rel="external noreferrer">
          {published.link} <IconExternalLink size={14} />
        </a>
      </article>
    );
  }

  return (
    <form className="card form staff-editor" onSubmit={(e) => void submit(e)}>
      <h3>New proposal</h3>
      <div className="form-grid">
        <label className="field">
          <span className="field-label">Brand</span>
          <select value={form.brand} onChange={set("brand")}>
            <option value="azores-getaways">Azores Getaways</option>
            <option value="portugal-getaways">Portugal Getaways</option>
            <option value="yes-getaways">Yes Getaways</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Holder email</span>
          <input type="email" value={form.holderEmail} onChange={set("holderEmail")} required />
        </label>
        <label className="field">
          <span className="field-label">Holder name</span>
          <input value={form.holderName} onChange={set("holderName")} />
        </label>
        <label className="field">
          <span className="field-label">Language</span>
          <select value={form.language} onChange={set("language")}>
            {["pt", "en", "es", "fr", "de"].map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Title</span>
          <input value={form.title} onChange={set("title")} required />
        </label>
        <label className="field">
          <span className="field-label">Destination</span>
          <input value={form.destination} onChange={set("destination")} />
        </label>
        <label className="field">
          <span className="field-label">Starts on</span>
          <input type="date" value={form.startsOn} onChange={set("startsOn")} />
        </label>
        <label className="field">
          <span className="field-label">Ends on</span>
          <input type="date" value={form.endsOn} onChange={set("endsOn")} />
        </label>
        <label className="field">
          <span className="field-label">Travellers</span>
          <input type="number" min={1} value={form.travellers} onChange={set("travellers")} />
        </label>
        <label className="field">
          <span className="field-label">Currency</span>
          <input value={form.currency} onChange={set("currency")} maxLength={3} />
        </label>
        <label className="field">
          <span className="field-label">Consultant name</span>
          <input value={form.consultantName} onChange={set("consultantName")} />
        </label>
        <label className="field">
          <span className="field-label">Valid until</span>
          <input type="date" value={form.validUntil} onChange={set("validUntil")} />
        </label>
        <label className="field">
          <span className="field-label">Deposit %</span>
          <input type="number" min={0} max={100} value={form.depositPercent} onChange={set("depositPercent")} />
        </label>
        <label className="field">
          <span className="field-label">Cover URL</span>
          <input type="url" value={form.coverUrl} onChange={set("coverUrl")} />
        </label>
      </div>
      <label className="field">
        <span className="field-label">Summary</span>
        <textarea value={form.summary} onChange={set("summary")} rows={2} />
      </label>
      <label className="field">
        <span className="field-label">Options (JSON)</span>
        <textarea value={options} onChange={(e) => setOptions(e.target.value)} rows={14} className="mono" spellCheck={false} />
      </label>
      <label className="field">
        <span className="field-label">Story (JSON, optional)</span>
        <textarea value={story} onChange={(e) => setStory(e.target.value)} rows={6} className="mono" spellCheck={false} />
      </label>
      {error && <p className="error-note">{error}</p>}
      <div>
        <button type="submit" className="button button-primary" disabled={busy}>
          {busy ? "Publishing…" : "Publish"}
        </button>
      </div>
    </form>
  );
}

function EventsTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error } = useStaffData(() => staffApi.events(), onError, []);
  const events = [...(data?.events ?? [])].sort((a, b) => b.seq - a.seq);
  return (
    <section>
      {error && <p className="error-note">{error}</p>}
      <div className="stack">
        {events.map((e: StaffEvent) => (
          <details key={e.seq} className="card event-card">
            <summary>
              <code>#{e.seq}</code> <strong>{e.type}</strong> <span className="muted small">{e.aggregate} {e.aggregateId}</span>{" "}
              <span className="muted small">{formatDateTime(e.createdAt, "en")}</span>
            </summary>
            <pre className="message-pre">{JSON.stringify(e.payload, null, 2)}</pre>
          </details>
        ))}
      </div>
    </section>
  );
}

function StatusTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error } = useStaffData(() => staffApi.status(), onError, []);
  const assistant = data && typeof data.assistant === "string" ? data.assistant : null;
  return (
    <section className="stack">
      {error && <p className="error-note">{error}</p>}
      {assistant && (
        <p className="small">
          Assistant provider: <span className="chip">{assistant}</span>
        </p>
      )}
      <ProgrammeBlock onError={onError} />
      <pre className="card message-pre">{data ? JSON.stringify(data, null, 2) : "…"}</pre>
    </section>
  );
}

// ── C3: the customers' threads and the consultant's reply ─────────────────────

const UNKNOWN_IDENTITY = "unknown";

/** Threads grouped by identity (newest first); open one to read it and answer as the consultant. */
function ThreadsTab({ onError }: { onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.threads(), onError, []);
  const identities = useStaffData(() => staffApi.identities(), onError, []);
  const [selected, setSelected] = useState<string | null>(null);

  const emailOf = new Map<string, string>();
  for (const i of identities.data?.identities ?? []) emailOf.set(i.id, i.email);

  const groups = new Map<string, ThreadMessage[]>();
  for (const m of [...(data?.messages ?? [])].sort((a, b) => b.at.localeCompare(a.at))) {
    const key = m.identityId ?? UNKNOWN_IDENTITY;
    const list = groups.get(key) ?? [];
    list.push(m);
    groups.set(key, list);
  }

  return (
    <section className="stack">
      <div className="section-title">
        <h2>Threads ({groups.size})</h2>
        <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
          <IconRefresh size={18} />
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
      {data && groups.size === 0 && <p className="muted">No messages yet.</p>}
      {groups.has(UNKNOWN_IDENTITY) && <p className="muted small">Some messages carry no identityId in the listing; they are grouped under "unknown". Open a thread by identity below instead.</p>}
      <div className="row-buttons">
        <span className="muted small">Open a thread by identity:</span>
        <select className="inline-input" value={selected ?? ""} onChange={(e) => setSelected(e.target.value || null)}>
          <option value="">—</option>
          {(identities.data?.identities ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.email}
            </option>
          ))}
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Messages</th>
              <th>Unread</th>
              <th>Last message</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {[...groups.entries()].map(([identityId, messages]) => {
              const latest = messages[0];
              const unread = messages.filter((m) => m.direction === "in" && !m.readAt).length;
              return (
                <tr key={identityId} className={`clickable ${selected === identityId ? "selected" : ""}`} onClick={() => setSelected(selected === identityId ? null : identityId)}>
                  <td>
                    {emailOf.get(identityId) ?? "—"} <code className="small">{identityId}</code>
                  </td>
                  <td>{messages.length}</td>
                  <td>{unread || "—"}</td>
                  <td className="cell-clip">
                    {latest ? (
                      <>
                        <span className={`chip chip-${latest.channel}`}>{latest.channel}</span> {latest.author}: {latest.body}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{latest ? relativeTime(latest.at, "en") : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selected && selected !== UNKNOWN_IDENTITY && <StaffThreadView identityId={selected} email={emailOf.get(selected)} onError={onError} onChanged={reload} />}
    </section>
  );
}

function StaffThreadView({ identityId, email, onError, onChanged }: { identityId: string; email: string | undefined; onError: (err: unknown) => void; onChanged: () => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.threads(identityId), onError, [identityId]);
  const [text, setText] = useState("");
  const [by, setBy] = useState("Your consultant");
  const [via, setVia] = useState<ReplyVia | "">("");
  const [busy, setBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const messages = [...(data?.messages ?? [])].sort((a, b) => a.at.localeCompare(b.at));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setReplyError(null);
    try {
      await staffApi.replyThread(identityId, { text: text.trim(), by: by.trim() || "Your consultant", ...(via ? { via } : {}) });
      setText("");
      reload();
      onChanged();
    } catch (err) {
      onError(err);
      setReplyError(err instanceof ApiError ? `${err.code}${err.message && err.message !== err.code ? `: ${err.message}` : ""}` : err instanceof Error ? err.message : "failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card staff-detail stack">
      <div className="section-title">
        <h2>{email ?? identityId}</h2>
        <button type="button" className="icon-button" onClick={reload} aria-label="Refresh">
          <IconRefresh size={18} />
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
      {data && messages.length === 0 && <p className="muted small">No messages on this thread.</p>}
      <ol className="thread thread-staff">
        {messages.map((m) => (
          <li key={m.id} className={`bubble-row ${m.author === "customer" ? "bubble-left" : "bubble-right"}`}>
            <div className={`bubble bubble-${m.author}`}>
              <span className="bubble-label">
                {m.authorName ?? m.author} · {m.channel}
                {m.kind !== "message" ? ` · ${m.kind}` : ""}
                {m.tripId ? ` · trip ${m.tripId.slice(0, 8)}` : ""}
              </span>
              {m.subject && <strong className="bubble-subject">{m.subject}</strong>}
              <p className="bubble-body">{m.body}</p>
              <span className="bubble-meta">
                <time dateTime={m.at}>{formatDateTime(m.at, "en")}</time>
                {m.direction === "out" && <span className="muted">{m.readAt ? " · read" : " · unread"}</span>}
              </span>
            </div>
          </li>
        ))}
      </ol>
      <form className="question-staff" onSubmit={(e) => void submit(e)}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply as the consultant" rows={2} required />
        <div className="row-buttons">
          <input value={by} onChange={(e) => setBy(e.target.value)} placeholder="By" className="inline-input" />
          <select value={via} onChange={(e) => setVia(e.target.value as ReplyVia | "")} className="inline-input">
            <option value="">Customer's channel</option>
            <option value="area">In the area</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <button type="submit" className="button button-small button-primary" disabled={busy || !text.trim()}>
            {busy ? "…" : "Reply"}
          </button>
        </div>
        {replyError && <p className="error-note">{replyError}</p>}
      </form>
    </article>
  );
}
